/**
 * Read-only conversation thread for a lost-lead campaign target — Engine v2 Phase 4.
 *
 * Mirrors the Leads conversation panel (LeadDetail.jsx) but view-only: it polls
 * the target customer's interactions every 3s and renders them as chat bubbles.
 * Human take-over (pause AI + manual send) arrives in Phase 5.
 */
import { useEffect, useRef, useState } from 'react';
import { formatInOrgTz } from '../lib/datetime';
import { deadLeadsService } from '../lib/services';
import { useToast } from '../lib/toast';

function fmtTime(iso) {
  return formatInOrgTz(iso, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }, '');
}

export default function DeadLeadConversation({ open, targetId, onClose }) {
  const { toast } = useToast();
  const [turns, setTurns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [takeover, setTakeover] = useState(false);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  const reload = () => deadLeadsService.targetConversation(targetId)
    .then(rows => setTurns(Array.isArray(rows) ? rows : [])).catch(() => {});

  const onTakeover = async () => {
    setBusy(true);
    try { await deadLeadsService.pauseAi(targetId); setTakeover(true); toast('AI paused — you are handling this conversation', { variant: 'info' }); }
    catch (e) { toast(`Take over failed: ${e?.message || 'error'}`, { variant: 'danger' }); }
    finally { setBusy(false); }
  };
  const onHandBack = async () => {
    setBusy(true);
    try { await deadLeadsService.resumeAi(targetId); setTakeover(false); toast('Handed back to AI', { variant: 'success' }); }
    catch (e) { toast(`Hand back failed: ${e?.message || 'error'}`, { variant: 'danger' }); }
    finally { setBusy(false); }
  };
  const onSend = async () => {
    if (!draft.trim()) return;
    setBusy(true);
    try { await deadLeadsService.replyTarget(targetId, draft.trim()); setDraft(''); await reload(); toast('Reply sent', { variant: 'success' }); }
    catch (e) { toast(`Send failed: ${e?.message || 'error'}`, { variant: 'danger' }); }
    finally { setBusy(false); }
  };

  useEffect(() => {
    if (!open || !targetId) return;
    let cancelled = false;
    const load = () => {
      if (document.visibilityState === 'hidden') return;
      setLoading(true);
      deadLeadsService.targetConversation(targetId)
        .then(rows => { if (!cancelled) setTurns(Array.isArray(rows) ? rows : []); })
        .catch(() => { if (!cancelled) setTurns([]); })
        .finally(() => { if (!cancelled) setLoading(false); });
    };
    load();
    const t = setInterval(load, 3000);
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => { cancelled = true; clearInterval(t); window.removeEventListener('keydown', onKey); };
  }, [open, targetId, onClose]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [turns]);

  if (!open) return null;

  return (
    <div role="dialog" aria-modal="true" aria-labelledby="dlc-title"
         onClick={onClose} className="modal-backdrop">
      <div className="modal" onClick={(e) => e.stopPropagation()}
           style={{ width: 'min(560px, 94vw)', display: 'flex', flexDirection: 'column', maxHeight: '80vh' }}>
        <div className="modal-header">
          <div>
            <div id="dlc-title" style={{ fontSize: 16, fontWeight: 600 }}>Conversation</div>
            <div className="mono text-xs text-muted" style={{ marginTop: 2 }}>
              Target #{targetId}{loading ? ' · refreshing…' : ''}
            </div>
          </div>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div ref={scrollRef} className="modal-body"
             style={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {turns.length === 0 && (
            <div className="text-muted text-xs" style={{ textAlign: 'center', padding: 20 }}>
              No messages yet for this target.
            </div>
          )}
          {turns.map((t) => {
            const inbound = t.direction === 'INBOUND';
            return (
              <div key={t.id} style={{ display: 'flex', justifyContent: inbound ? 'flex-end' : 'flex-start' }}>
                <div className={`chat-bubble ${inbound ? 'customer' : 'ai'}`} style={{ maxWidth: '80%' }}>
                  {t.body || t.transcript || '(no text)'}
                  <div className="chat-meta">
                    {inbound ? 'Customer' : 'AI/Dealer'} · {t.channel} · {fmtTime(t.occurredAt)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ borderTop: '1px solid var(--border-color)', padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {!takeover ? (
            <div className="flex-between">
              <span className="mono text-xs text-muted">AI is handling this conversation.</span>
              <button className="btn small" disabled={busy} onClick={onTakeover}>Take over from AI</button>
            </div>
          ) : (
            <>
              <div className="flex-between">
                <span className="mono text-xs" style={{ color: 'var(--warning)' }}>⏸ AI paused — you're replying</span>
                <button className="btn small" disabled={busy} onClick={onHandBack}>Hand back to AI</button>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') onSend(); }}
                  placeholder="Type a reply…"
                  style={{ flex: 1, padding: '8px 12px', background: 'var(--bg-glass)',
                           border: '1px solid var(--border-color)', borderRadius: 'var(--radius-sm)',
                           color: 'var(--text-primary)', fontSize: 13, outline: 'none' }}
                />
                <button className="btn small primary" disabled={busy || !draft.trim()} onClick={onSend}>Send</button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
